
import React, { useState, useEffect } from 'react';
import { UsageSummary, DashboardOverviewResponse, WooCommerceSyncStatus, ValueScorecard as ValueScorecardData } from '../types';
import Card from '../components/ui/Card';
import { api } from '../services/api';
import UsageComposedChart from '../components/ui/UsageComposedChart';
import { CartValueKPI } from '../components/ui/CartValueKPI';
import { SalesPerformanceKPI } from '../components/ui/SalesPerformanceKPI';
import { ValueScorecard } from '../components/ui/ValueScorecard';
import ConversationUsageProgressBar from '../components/ui/LLMUsageProgressBar';
import LLMControlButton from '../components/ui/LLMControlButton';
import { MessageSquare, Users, Clock, ArrowRight, Server, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSubscriptionError } from '../context/SubscriptionErrorContext';
import { useAuth } from '../hooks/useAuth';
import { handleApiError } from '../utils/errorHandling';
import { getDisplayPercentage } from '../utils/percentageUtils';
import { ViewDetailsButton } from '../components/ui/ViewDetailsButton';
import { useInsightFormula } from '../hooks/useInsightFormula';
import Modal from '../components/ui/Modal';

const DashboardPage: React.FC = () => {
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardOverviewResponse | null>(null);
  const [previousMonthData, setPreviousMonthData] = useState<DashboardOverviewResponse | null>(null);
  const [syncStatus, setSyncStatus] = useState<WooCommerceSyncStatus | null>(null);
  const [scorecard, setScorecard] = useState<ValueScorecardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<{ title: string; type: string } | null>(null);
  const [dateOffset, setDateOffset] = useState(0);
  const [cartValueDateOffset, setCartValueDateOffset] = useState(0);
  const [salesPerfDateOffset, setSalesPerfDateOffset] = useState(0);
  const [sdkEnabledLocal, setSdkEnabledLocal] = useState<boolean | null>(null);
  const { setSubscriptionError } = useSubscriptionError();
  const { user } = useAuth();

  const calculateDelta = (current: number, previous: number): number => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const usageData = await api.get<UsageSummary>('/api/subscriptions/tenant/usage');
        setUsage(usageData);
        setSdkEnabledLocal(usageData.sdkStatus?.enabled ?? true);

        const formatDate = (date: Date) => {
          return date.toISOString().split('T')[0];
        };

        // Current period: let backend use subscription cycle by not sending dates
        // Previous period: same day last month to 30 days before that
        const previousEndDate = new Date();
        previousEndDate.setMonth(previousEndDate.getMonth() - 1);
        const previousStartDate = new Date(previousEndDate);
        previousStartDate.setDate(previousStartDate.getDate() - 30);

        const [currentData, previousData] = await Promise.all([
          api.getDashboardOverview(), // Default to current cycle
          api.getDashboardOverview(
            formatDate(previousStartDate),
            formatDate(previousEndDate)
          )
        ]);

        setDashboardData(currentData);
        setPreviousMonthData(previousData);

        try {
          const status = await api.getWooCommerceStatus();
          setSyncStatus(status);
        } catch {
          // Integration may not be configured yet
        }

        try {
          setScorecard(await api.getValueScorecard());
        } catch {
          // A tenant with no polled orders yet has nothing to compare; the pane stays hidden
          // rather than showing an error for a state that is simply "too early".
        }

        setError(null);
        setSubscriptionError(null);
      } catch (err: any) {
        handleApiError(err, setError, setSubscriptionError);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
        <div className="flex justify-center items-center h-full">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
    );
  }

  if (error) {
      return <div className="text-center text-red-500">{error}</div>
  }
  
  if (!usage || !dashboardData) {
      return <div className="text-center text-text-secondary">No data available.</div>
  }

  const billingPeriodStart = new Date(usage.subscription.billing.currentPeriodStart);
  const billingPeriodEnd = new Date(usage.subscription.billing.currentPeriodEnd);

  // Helper to get date window for filtering (relative to today)
  const getDateWindow = (offset: number) => {
    const end = new Date();
    end.setDate(end.getDate() + offset);
    
    // Ensure end doesn't go before billing period start
    const effectiveEnd = end < billingPeriodStart ? new Date(billingPeriodStart) : end;
    
    const start = new Date(effectiveEnd);
    start.setDate(start.getDate() - 6);
    
    // Ensure start doesn't go before billing period start
    const effectiveStart = start < billingPeriodStart ? new Date(billingPeriodStart) : start;
    
    return {
      start: effectiveStart.toISOString().split('T')[0],
      end: effectiveEnd.toISOString().split('T')[0]
    };
  };

  const usageWindow = getDateWindow(dateOffset);
  const cartValueWindow = getDateWindow(cartValueDateOffset);
  const salesPerfWindow = getDateWindow(salesPerfDateOffset);

  // Filter series to only include dates within current billing period
  const costSeriesInPeriod = (dashboardData?.data?.usageOverview?.dailyCostBreakdown || []).filter(
    entry => entry.date >= billingPeriodStart.toISOString().split('T')[0]
  );

  const usageChartData = costSeriesInPeriod
    .filter(day => day.date >= usageWindow.start && day.date <= usageWindow.end)
    .map(day => {
      const planLimit = usage?.llmUsage?.limit ?? usage?.llmUsageCost?.included ?? 0;
      const beProvidedPct = (day as any).dailyUsagePercentage as number | undefined;
      const fallbackPct = planLimit > 0 ? (day.cost / planLimit) * 100 : 0;
      return {
        time: day.date,
        usage: day.tokens,
        dailyCost: day.cost,
        cumulativeCost: day.cumulativeCost,
        dailyUsagePct: beProvidedPct ?? fallbackPct,
      };
    });
  
  // Extract current and previous period costs for trend calculation
  const currentCostTotal = dashboardData?.data?.usageOverview?.totals?.cost || 0;
  const previousCostTotal = previousMonthData?.data?.usageOverview?.totals?.cost || 0;
  
  const costMetrics = {
    cumulative: currentCostTotal,
    avgPerDay: dashboardData?.data?.usageOverview?.averages?.costPerDay || 0,
    projected: dashboardData?.data?.usageOverview?.projectedUsageCost || dashboardData?.data?.usageOverview?.projectedMonthlyCost || 0,
    trendPct: calculateDelta(currentCostTotal, previousCostTotal)
  };

  // ---- LLM usage / package-type derivation -------------------------------
  // BE today returns: usage.packageType, usage.llmUsageCost { included, used, extra, showExtraCost }
  // BE proposed:      usage.llmUsage { limit, used, usagePercentage, overageAmount, ... }
  // We read from the new shape first, then fall back to today's shape.
  const packageType: 'fixed' | 'custom' =
    (usage as any)?.packageType === 'custom' ? 'custom' : 'fixed';
  const isFixed = packageType === 'fixed';

  const llmLimit =
    usage.llmUsage?.limit ?? usage.llmUsageCost?.included ?? 0;
  const llmUsed =
    usage.llmUsage?.used ?? usage.llmUsageCost?.used ?? currentCostTotal;
  const llmOverage =
    usage.llmUsage?.overageAmount ?? usage.llmUsageCost?.extra ?? 0;
  const llmUsagePct =
    usage.llmUsage?.usagePercentage ??
    (llmLimit > 0 ? (llmUsed / llmLimit) * 100 : 0);
  const isOverLimit =
    (usage.llmUsageCost?.showExtraCost ?? false) || llmOverage > 0 || llmUsagePct >= 100;
  const warningThresholdPct = usage.llmUsage?.warningThresholdPct ?? 80;

  // For fixed packages we hide cumulative € on the chart until they surpass the limit.
  const chartDisplayMode: 'cost' | 'percentage' =
    isFixed && !isOverLimit ? 'percentage' : 'cost';

  // SDK / agent kill-switch (BE: POST /api/subscriptions/tenant/sdk-status)
  const sdkEnabled = sdkEnabledLocal ?? usage.sdkStatus?.enabled ?? true;
  const canToggleSdk = usage.sdkStatus?.canToggle ?? false;

  const handleToggleAgent = async (nextEnabled: boolean) => {
    const res = await api.setSdkStatus(nextEnabled);
    setSdkEnabledLocal(res.enabled);
  };
  
  const currentProductMentions = dashboardData?.data?.productInterest?.sessionsWithProductMentions || 0;
  const previousProductMentions = previousMonthData?.data?.productInterest?.sessionsWithProductMentions || 0;
  
  // Transform cost series for AreaChart
  const cartValueSeries = costSeriesInPeriod
    .filter(entry => entry.date >= cartValueWindow.start && entry.date <= cartValueWindow.end)
    .map(entry => ({
      date: entry.date,
      value: entry.cumulativeCost
    }));
  
  const cartValueData = {
    total: currentProductMentions,
    deltaPct: calculateDelta(currentProductMentions, previousProductMentions),
    series: cartValueSeries
  };

  const currentConversionRate = dashboardData?.data?.salesPerformance?.conversionRate || 0;
  const previousConversionRate = previousMonthData?.data?.salesPerformance?.conversionRate || 0;
  
  const currentOrders = dashboardData?.data?.salesPerformance?.ordersGenerated || 0;
  const previousOrders = previousMonthData?.data?.salesPerformance?.ordersGenerated || 0;
  
  const currentAvgOrderValue = dashboardData?.data?.salesPerformance?.avgOrderValue || 0;
  const previousAvgOrderValue = previousMonthData?.data?.salesPerformance?.avgOrderValue || 0;
  
  const currentConversationsWithOrder = dashboardData?.data?.salesPerformance?.conversationsWithOrder || 0;
  const previousConversationsWithOrder = previousMonthData?.data?.salesPerformance?.conversationsWithOrder || 0;
  
  const currentRevenue = dashboardData?.data?.salesPerformance?.revenue || 0;
  const previousRevenue = previousMonthData?.data?.salesPerformance?.revenue || 0;
  
  // Transform sales performance series for ComposedChart
  const salesPerfSeries = (dashboardData?.data?.salesPerformance?.series || [])
    .filter(entry => entry.date >= salesPerfWindow.start && entry.date <= salesPerfWindow.end)
    .map(entry => ({
      date: entry.date,
      conversionRate: entry.conversionRate,
      orders: entry.orders
    }));
  
  const salesPerfData = {
    conversionRate: currentConversionRate,
    deltaPct: calculateDelta(currentConversionRate, previousConversionRate),
    orders: currentOrders,
    ordersDelta: calculateDelta(currentOrders, previousOrders),
    carts: currentConversationsWithOrder,
    cartsDelta: calculateDelta(currentConversationsWithOrder, previousConversationsWithOrder),
    avgOrderValue: currentAvgOrderValue,
    avgOrderValueDelta: calculateDelta(currentAvgOrderValue, previousAvgOrderValue),
    revenue: currentRevenue,
    revenueDelta: calculateDelta(currentRevenue, previousRevenue),
    series: salesPerfSeries
  };

  const currentTotalConversations = dashboardData?.data?.usageOverview?.conversationSummary?.total || 0;
  const previousTotalConversations = previousMonthData?.data?.usageOverview?.conversationSummary?.total || 0;
  const conversationsDelta = calculateDelta(currentTotalConversations, previousTotalConversations);

  const currentSDKConversations = dashboardData?.data?.usageOverview?.conversationSummary?.sdk || 0;
  const previousSDKConversations = previousMonthData?.data?.usageOverview?.conversationSummary?.sdk || 0;
  const sdkConversationsDelta = calculateDelta(currentSDKConversations, previousSDKConversations);

  const currentMerchantConversations = dashboardData?.data?.usageOverview?.conversationSummary?.merchant || 0;
  const previousMerchantConversations = previousMonthData?.data?.usageOverview?.conversationSummary?.merchant || 0;
  const merchantConversationsDelta = calculateDelta(currentMerchantConversations, previousMerchantConversations);

  const currentSDKActiveUsers = dashboardData?.data?.usageOverview?.activeUsersByChannel?.sdk || 0;
  const previousSDKActiveUsers = previousMonthData?.data?.usageOverview?.activeUsersByChannel?.sdk || 0;
  const sdkActiveUsersDelta = calculateDelta(currentSDKActiveUsers, previousSDKActiveUsers);
  
  const getDateRange = () => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + dateOffset);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 6);
    
    const formatDate = (date: Date) => {
      return `${date.getDate()}/${date.getMonth() + 1}`;
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const getCartValueDateRange = () => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + cartValueDateOffset);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 6);
    
    const formatDate = (date: Date) => {
      return `${date.getDate()}/${date.getMonth() + 1}`;
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const handlePreviousWeek = () => {
    const newOffset = dateOffset - 7;
    const newEndDate = new Date();
    newEndDate.setDate(newEndDate.getDate() + newOffset);
    
    // Only allow going back if the end of the new window is after or at the billing period start
    if (newEndDate >= billingPeriodStart) {
      setDateOffset(newOffset);
    }
  };

  const handleNextWeek = () => {
    if (dateOffset < 0) {
      setDateOffset(Math.min(dateOffset + 7, 0));
    }
  };

  const isAtPresent = dateOffset === 0;
  
  const checkIsAtStart = (offset: number) => {
    const d = new Date();
    d.setDate(d.getDate() + offset - 7);
    return d < billingPeriodStart;
  };

  const isAtBillingCycleStart = checkIsAtStart(dateOffset);

  const handleCartValuePreviousWeek = () => {
    const newOffset = cartValueDateOffset - 7;
    const newEndDate = new Date();
    newEndDate.setDate(newEndDate.getDate() + newOffset);
    if (newEndDate >= billingPeriodStart) {
      setCartValueDateOffset(newOffset);
    }
  };

  const handleCartValueNextWeek = () => {
    if (cartValueDateOffset < 0) {
      setCartValueDateOffset(Math.min(cartValueDateOffset + 7, 0));
    }
  };

  const isCartValueAtPresent = cartValueDateOffset === 0;
  const isCartValueAtBillingCycleStart = checkIsAtStart(cartValueDateOffset);

  const handleSalesPerfPreviousWeek = () => {
    const newOffset = salesPerfDateOffset - 7;
    const newEndDate = new Date();
    newEndDate.setDate(newEndDate.getDate() + newOffset);
    if (newEndDate >= billingPeriodStart) {
      setSalesPerfDateOffset(newOffset);
    }
  };

  const handleSalesPerfNextWeek = () => {
    if (salesPerfDateOffset < 0) {
      setSalesPerfDateOffset(Math.min(salesPerfDateOffset + 7, 0));
    }
  };

  const isSalesPerfAtPresent = salesPerfDateOffset === 0;
  const isSalesPerfAtBillingCycleStart = checkIsAtStart(salesPerfDateOffset);

  const getSalesPerfDateRange = () => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + salesPerfDateOffset);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 6);
    
    const formatDate = (date: Date) => {
      return `${date.getDate()}/${date.getMonth() + 1}`;
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const { getAIInsight } = useInsightFormula(usage);

  const handleViewDetails = (type: 'cumulative' | 'cartValue' | 'salesPerformance') => {
    const titles = {
      cumulative: 'Usage Details',
      cartValue: 'Product Interest Analytics',
      salesPerformance: 'Purchase Intent Details',
    };
    setModalContent({ title: titles[type], type });
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-8 pb-8">
      <div>
        <h1 className="text-3xl font-bold dark:text-text-primary text-light-text-primary">Dashboard</h1>
        <p className="dark:text-text-secondary text-light-text-secondary">
          Welcome back{user?.personalInfo?.firstName ? ` ${user.personalInfo.firstName}` : ''}! Here's a summary of your account.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Usage Card */}
        <Card title="Usage Overview via Mentoros" className="lg:col-span-2">
          {/* Package-aware LLM usage header: progress bar (fixed only) + STOP/PLAY (both) */}
          <div className="px-4 pt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              {isFixed ? (
                <ConversationUsageProgressBar
                  limit={llmLimit}
                  used={llmUsed}
                  overage={llmOverage}
                  warningThresholdPct={warningThresholdPct}
                />
              ) : (
                <p className="text-sm dark:text-text-secondary text-light-text-secondary">
                  Flex plan — every conversation is billed, up to the ceiling you set.
                </p>
              )}
            </div>
            <div className="flex-shrink-0">
              <LLMControlButton
                enabled={sdkEnabled}
                canToggle={canToggleSdk}
                onToggle={handleToggleAgent}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
            {/* Usage Chart with Button */}
            <div className="flex flex-col">
              <UsageComposedChart 
                data={usageChartData}
                title="Usage"
                dateRange={getDateRange()}
                onPrevious={handlePreviousWeek}
                onNext={handleNextWeek}
                canGoPrevious={!isAtBillingCycleStart}
                canGoNext={!isAtPresent}
                onReturnToPresent={() => setDateOffset(0)}
                isAtPresent={isAtPresent}
                displayMode={chartDisplayMode}
              />
              <div className="flex justify-center mt-4">
                <ViewDetailsButton label="View Details" onClick={() => handleViewDetails('cumulative')} />
              </div>
            </div>

            {/* Product Interest Chart with Button */}
            <div className="flex flex-col">
              <CartValueKPI 
                title="Product Interest"
                total={cartValueData.total}
                deltaPct={cartValueData.deltaPct}
                series={cartValueData.series}
                dateRange={getCartValueDateRange()}
                onPrevious={handleCartValuePreviousWeek}
                onNext={handleCartValueNextWeek}
                canGoPrevious={!isCartValueAtBillingCycleStart}
                canGoNext={!isCartValueAtPresent}
                onReturnToPresent={() => setCartValueDateOffset(0)}
                isAtPresent={isCartValueAtPresent}
                displayMode="percentage"
                subtitle="Sessions with product mentions"
              />
              <div className="flex justify-center mt-4">
                <ViewDetailsButton label="View Details" onClick={() => handleViewDetails('cartValue')} />
              </div>
            </div>

            {/* Sales Performance Chart with Button */}
            <div className="flex flex-col text-center">
              <SalesPerformanceKPI
                conversionRate={salesPerfData.conversionRate}
                deltaPct={salesPerfData.deltaPct}
                orders={salesPerfData.orders}
                ordersDelta={salesPerfData.ordersDelta}
                avgOrderValue={salesPerfData.avgOrderValue}
                avgOrderValueDelta={salesPerfData.avgOrderValueDelta}
                series={salesPerfData.series}
                title="Purchase Intent"
                dateRange={getSalesPerfDateRange()}
                onPrevious={handleSalesPerfPreviousWeek}
                onNext={handleSalesPerfNextWeek}
                canGoPrevious={!isSalesPerfAtBillingCycleStart}
                canGoNext={!isSalesPerfAtPresent}
                onReturnToPresent={() => setSalesPerfDateOffset(0)}
                isAtPresent={isSalesPerfAtPresent}
              />
              <div className="flex justify-center mt-4">
                <ViewDetailsButton label="View Details" onClick={() => handleViewDetails('salesPerformance')} />
              </div>
            </div>
          </div>

          {/* Assisted vs unassisted orders. Hidden until the reconciliation poll has store orders
              to compare — an empty pane would read as "the assistant did nothing" rather than
              "nothing has been measured yet". */}
          {scorecard && (
            <div className="mt-6">
              <ValueScorecard data={scorecard} />
            </div>
          )}

          <div className="mt-6 pt-6 border-t dark:border-border-color border-light-border-color grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm dark:text-text-secondary text-light-text-secondary">Cumulative (cycle)</p>
              {isFixed && !isOverLimit ? (
                <p className="text-2xl font-bold dark:text-text-secondary text-light-text-secondary">—</p>
              ) : (
                <p className="text-2xl font-bold dark:text-white text-gray-900">
                  €{costMetrics.cumulative.toFixed(2)}
                  {costMetrics.trendPct !== null && (
                    <span className={`ml-2 text-sm font-medium ${
                      costMetrics.trendPct > 0 
                        ? 'text-green-500' 
                        : costMetrics.trendPct < 0 
                        ? 'text-red-500' 
                        : 'dark:text-text-secondary text-light-text-secondary'
                    }`}>
                      ({costMetrics.trendPct > 0 ? '↑' : costMetrics.trendPct < 0 ? '↓' : '→'} {Math.abs(costMetrics.trendPct).toFixed(0)}%)
                    </span>
                  )}
                </p>
              )}
            </div>
            <div>
              <p className="text-sm dark:text-text-secondary text-light-text-secondary">Avg/day</p>
              <p className="text-2xl font-bold dark:text-white text-gray-900">€{costMetrics.avgPerDay.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm dark:text-text-secondary text-light-text-secondary">Projected (cycle)</p>
              <p className="text-2xl font-bold dark:text-white text-gray-900">
                €{costMetrics.projected.toFixed(2)}
              </p>
            </div>
          </div>
        </Card>

        {/* Side Cards */}
        <div className="space-y-6">
          <Card title="Subscription Details">
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium dark:text-text-secondary text-light-text-secondary">Current Plan</p>
                <p className="text-xl font-bold text-primary capitalize">{usage.subscription.planId.replace('-', ' ')}</p>
              </div>
              <div>
                <p className="text-sm font-medium dark:text-text-secondary text-light-text-secondary">Status</p>
                <p className={`text-lg font-semibold ${usage.subscription.status === 'active' ? 'text-green-400' : 'text-yellow-400'} capitalize`}>{usage.subscription.status}</p>
              </div>
              <div>
                <p className="text-sm font-medium dark:text-text-secondary text-light-text-secondary">Billing Period</p>
                <div className="flex items-center text-sm dark:text-text-primary text-light-text-primary gap-2">
                  <span>{new Date(usage.subscription.billing.currentPeriodStart).toLocaleDateString()}</span>
                  <ArrowRight className="w-4 h-4 dark:text-text-secondary text-light-text-secondary"/>
                  <span>{new Date(usage.subscription.billing.currentPeriodEnd).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="dark:bg-background bg-gray-100 p-4 rounded-lg text-center">
                <p className="text-sm dark:text-text-secondary text-light-text-secondary">Next billing in</p>
                <p className="text-3xl font-bold dark:text-white text-gray-900">{usage.daysUntilReset}</p>
                <p className="text-sm dark:text-text-secondary text-light-text-secondary">days</p>
              </div>
            </div>
          </Card>

          <Card title="AI Performance Metrics">
            <ul className="space-y-7 text-sm">
              <li className="flex justify-between items-center">
                <span className="flex items-center gap-2 dark:text-text-secondary text-light-text-secondary">
                  <span className="text-lg">✅</span> Question Resolution Rate
                </span>
                <span className="font-semibold text-yellow-500">{(100 - (dashboardData?.data?.aiPerformance?.errorRate || 0))?.toFixed(0)}%</span>
              </li>
              <li className="flex justify-between items-center">
                <span className="flex items-center gap-2 dark:text-text-secondary text-light-text-secondary">
                  <span className="text-lg">😊</span> Customer Satisfaction
                </span>
                <span className="font-semibold text-purple-500">{dashboardData?.data?.aiPerformance?.customerSatisfaction?.toFixed(1) || 0}/5</span>
              </li>
              <li className="flex justify-between items-center">
                <span className="flex items-center gap-2 dark:text-text-secondary text-light-text-secondary">
                  <span className="text-lg">⚡</span> Average Response Time
                </span>
                <span className="font-semibold text-blue-500">
                  {dashboardData?.data?.aiPerformance?.avgResponseTime 
                    ? `${(dashboardData.data.aiPerformance.avgResponseTime / 1000).toFixed(2)}s`
                    : '0s'
                  }
                </span>
              </li>
              <li className="flex justify-between items-center">
                <span className="flex items-center gap-2 dark:text-text-secondary text-light-text-secondary">
                  <span className="text-lg">📦</span> Knowledge Freshness
                </span>
                <span className="font-semibold text-green-500">
                  {syncStatus?.lastSuccessfulSyncAt
                    ? `Last Sync: ${new Date(syncStatus.lastSuccessfulSyncAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${new Date(syncStatus.lastSuccessfulSyncAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`
                    : 'No sync yet'
                  }
                </span>
              </li>
            </ul>
          </Card>
        </div>
      </div>

      {/* Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={modalContent?.title}
      >
        {modalContent?.type === 'cumulative' && dashboardData && (
          <div className="space-y-6">
            {/* Usage Split */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Usage Distribution</h3>
              <div className="dark:bg-surface bg-light-surface p-6 rounded-lg border dark:border-border-color border-light-border-color">
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium dark:text-text-primary text-light-text-primary">Merchant</span>
                      <span className="text-lg font-bold text-primary">{dashboardData?.data?.usageOverview?.usageDistribution?.merchantPercentage?.toFixed(0) || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div className="bg-gradient-to-r from-primary to-primary-hover h-full rounded-full" style={{ width: `${dashboardData?.data?.usageOverview?.usageDistribution?.merchantPercentage || 0}%` }}></div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium dark:text-text-primary text-light-text-primary">SDK</span>
                      <span className="text-lg font-bold text-green-500">{dashboardData?.data?.usageOverview?.usageDistribution?.sdkPercentage?.toFixed(0) || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                      <div className="bg-gradient-to-r from-green-500 to-green-600 h-full rounded-full" style={{ width: `${dashboardData?.data?.usageOverview?.usageDistribution?.sdkPercentage || 0}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Conversations Breakdown */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Conversations</h3>
              <div className="dark:bg-surface bg-light-surface p-6 rounded-lg border dark:border-border-color border-light-border-color">
                <div className="text-center mb-4">
                  <p className="text-sm dark:text-text-secondary text-light-text-secondary mb-1">Total Conversations</p>
                  <p className="text-5xl font-bold dark:text-white text-gray-900">{dashboardData?.data?.usageOverview?.conversationSummary?.total || 0}</p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <span className={`font-medium flex items-center ${conversationsDelta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      <span className="text-lg">{conversationsDelta >= 0 ? '↑' : '↓'}</span> {Math.abs(conversationsDelta).toFixed(1)}%
                    </span>
                    <span className="text-sm dark:text-text-secondary text-light-text-secondary">vs last month</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="p-4 dark:bg-background bg-gray-50 rounded-lg border dark:border-border-color border-gray-200">
                    <p className="text-xs dark:text-text-secondary text-light-text-secondary mb-1">From SDK</p>
                    <p className="text-3xl font-bold text-green-500">{dashboardData?.data?.usageOverview?.conversationSummary?.sdk || 0}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs dark:text-text-secondary text-light-text-secondary">{(((dashboardData?.data?.usageOverview?.conversationSummary?.sdk || 0) / (dashboardData?.data?.usageOverview?.conversationSummary?.total || 1)) * 100).toFixed(1)}%</p>
                      <span className={`text-xs font-medium flex items-center ${sdkConversationsDelta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {sdkConversationsDelta >= 0 ? '↑' : '↓'} {Math.abs(sdkConversationsDelta).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="p-4 dark:bg-background bg-gray-50 rounded-lg border dark:border-border-color border-gray-200">
                    <p className="text-xs dark:text-text-secondary text-light-text-secondary mb-1">From Merchant</p>
                    <p className="text-3xl font-bold text-primary">{dashboardData?.data?.usageOverview?.conversationSummary?.merchant || 0}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs dark:text-text-secondary text-light-text-secondary">{(((dashboardData?.data?.usageOverview?.conversationSummary?.merchant || 0) / (dashboardData?.data?.usageOverview?.conversationSummary?.total || 1)) * 100).toFixed(1)}%</p>
                      <span className={`text-xs font-medium flex items-center ${merchantConversationsDelta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {merchantConversationsDelta >= 0 ? '↑' : '↓'} {Math.abs(merchantConversationsDelta).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Usage Reasons */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Usage Reasons</h3>
              <div className="dark:bg-surface bg-light-surface p-6 rounded-lg border dark:border-border-color border-light-border-color">
                <div className="space-y-4">
                  {(() => {
                    const reasons = dashboardData?.data?.usageOverview?.usageReasons || [];
                    const colors = ['bg-primary', 'bg-green-500', 'bg-yellow-500', 'bg-blue-500', 'bg-purple-500', 'bg-red-500', 'bg-gray-400'];
                    const total = reasons.reduce((sum, r) => sum + r.count, 0);
                    return reasons.map((reason, index) => {
                      const pct = total > 0 ? (reason.count / total) * 100 : 0;
                      return (
                        <div key={reason.id}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium dark:text-text-primary text-light-text-primary">{reason.label}</span>
                            <span className="text-sm font-bold dark:text-white text-gray-900">{pct.toFixed(1)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                            <div className={`${colors[index % colors.length]} h-full rounded-full`} style={{ width: `${pct}%` }}></div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-4">Distribution of reasons users engaged with Mentoros</p>
              </div>
            </div>

            {/* Active Users */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Active Users</h3>
              <div className="dark:bg-surface bg-light-surface p-6 rounded-lg border dark:border-border-color border-light-border-color">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary-hover mb-4">
                    <Users className="w-12 h-12 text-white" />
                  </div>
                  <p className="text-sm dark:text-text-secondary text-light-text-secondary mb-1">SDK Active Users</p>
                  <p className="text-5xl font-bold dark:text-white text-gray-900">{dashboardData?.data?.usageOverview?.activeUsersByChannel?.sdk || 0}</p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <span className={`font-medium flex items-center text-sm ${sdkActiveUsersDelta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      <span className="text-base">{sdkActiveUsersDelta >= 0 ? '↑' : '↓'}</span> {Math.abs(sdkActiveUsersDelta).toFixed(1)}%
                    </span>
                    <span className="text-xs dark:text-text-secondary text-light-text-secondary">vs last month</span>
                  </div>
                  <p className="text-sm dark:text-text-secondary text-light-text-secondary mt-2">Users actively using the SDK</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {modalContent?.type === 'cartValue' && dashboardData && (
          <div className="space-y-6">
            {/* Product Interest Sessions - Primary Metric */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Product Interest Sessions</h3>
              <div className="dark:bg-surface bg-light-surface p-6 rounded-lg border dark:border-border-color border-light-border-color">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm dark:text-text-secondary text-light-text-secondary mb-1">Sessions with Product Mentions</p>
                    <p className="text-4xl font-bold text-primary">{(dashboardData?.data?.productInterest?.sessionsWithProductMentions || 0).toFixed(1)}%</p>
                  </div>
                  <span className={`text-xs rounded-full px-3 py-1 font-medium ${cartValueData.deltaPct >= 0 ? 'bg-green-500/15 text-green-600 dark:text-green-300' : 'bg-red-500/15 text-red-600 dark:text-red-300'}`}>
                    {cartValueData.deltaPct >= 0 ? '↑' : '↓'} {Math.abs(cartValueData.deltaPct).toFixed(1)}%
                  </span>
                </div>
                <p className="text-xs dark:text-text-secondary text-light-text-secondary mb-6">% of sessions where users mentioned a product or category</p>
                
                {/* Query Coverage */}
                <div className="mt-6 pt-6 border-t dark:border-border-color border-light-border-color">
                  <h4 className="text-sm font-semibold dark:text-text-primary text-light-text-primary mb-3">Query Coverage</h4>
                  {(() => {
                    const coverage = dashboardData?.data?.productInterest?.queryCoverage;
                    const rate = coverage?.coverageRate ?? 0;
                    return (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs sm:text-sm dark:text-text-secondary text-light-text-secondary">Successful queries</span>
                          <span className="text-sm font-semibold dark:text-text-primary text-light-text-primary">{coverage?.successful ?? 0} / {coverage?.total ?? 0}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div className="bg-primary h-full rounded-full" style={{ width: `${rate}%` }}></div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs dark:text-text-secondary text-light-text-secondary">Coverage rate</span>
                          <span className="text-sm font-bold text-primary">{rate.toFixed(1)}%</span>
                        </div>
                      </div>
                    );
                  })()}
                  <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-3">Percentage of product queries successfully answered</p>
                </div>
              </div>
            </div>

            {/* Mini KPIs */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Chat Engagement Metrics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color">
                  <p className="text-xs uppercase tracking-wider dark:text-text-secondary text-light-text-secondary mb-2">Total Product Mentions</p>
                  <p className="text-3xl font-bold dark:text-white text-gray-900">{(dashboardData?.data?.productInterest?.totalProductMentions || 0).toLocaleString()}</p>
                  <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">30 days</p>
                </div>
                
                <div className="p-4 dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color">
                  <p className="text-xs uppercase tracking-wider dark:text-text-secondary text-light-text-secondary mb-2">Unique Products Discussed</p>
                  <p className="text-3xl font-bold text-primary">{dashboardData?.data?.productInterest?.uniqueProducts || 0}</p>
                  <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">distinct SKUs</p>
                </div>
              </div>
            </div>

            {/* Price Intent Distribution */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Price Intent Distribution</h3>
              <div className="dark:bg-surface bg-light-surface p-6 rounded-lg border dark:border-border-color border-light-border-color">
                <div className="space-y-4">
                  {Object.entries(dashboardData?.data?.productInterest?.priceDistribution || {}).map(([range, count], index) => {
                    const total = Object.values(dashboardData?.data?.productInterest?.priceDistribution || {}).reduce((sum: number, val) => sum + (val as number), 0);
                    const percentage = (total as number) > 0 ? ((count as number) / (total as number) * 100).toFixed(1) : 0;
                    const item = { range, percentage };
                    const colors = [
                      { from: 'from-primary', to: 'to-primary-hover', text: 'text-primary' },
                      { from: 'from-green-500', to: 'to-green-600', text: 'text-green-500' },
                      { from: 'from-yellow-500', to: 'to-yellow-600', text: 'text-yellow-500' },
                      { from: 'from-purple-500', to: 'to-purple-600', text: 'text-purple-500' }
                    ];
                    const color = colors[index] || colors[0];
                    
                    return (
                      <div key={item.range}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium dark:text-text-primary text-light-text-primary">{item.range}</span>
                          <span className={`text-lg font-bold ${color.text}`}>{item.percentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                          <div className={`bg-gradient-to-r ${color.from} ${color.to} h-full rounded-full`} style={{ width: `${item.percentage}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-4">Distribution of mentioned/suggested product prices</p>
              </div>
            </div>

            {/* Top Products */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-4">Top Products</h3>
              <div className="space-y-3">
                {(dashboardData?.data?.productInterest?.topProducts || []).slice(0, 5).map((product, index) => {
                  const totalMentions = dashboardData?.data?.productInterest?.totalProductMentions || 1;
                  const percentage = ((product.mentions / totalMentions) * 100).toFixed(1);
                  
                  const colors = [
                    { from: 'from-primary', to: 'to-primary-hover', text: 'text-primary' },
                    { from: 'from-green-500', to: 'to-green-600', text: 'text-green-500' },
                    { from: 'from-yellow-500', to: 'to-yellow-600', text: 'text-yellow-500' },
                    { from: 'from-gray-400', to: 'to-gray-500', text: 'text-gray-600 dark:text-text-secondary' }
                  ];
                  const color = colors[index] || colors[colors.length - 1];
                  
                  return (
                    <div key={product.sku} className="p-4 dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium dark:text-text-primary text-light-text-primary truncate mr-2">{product.name}</span>
                        <span className={`text-lg font-bold ${color.text}`}>{product.mentions}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                        <div className={`bg-gradient-to-r ${color.from} ${color.to} h-full rounded-full`} style={{ width: `${percentage}%` }}></div>
                      </div>
                      <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">{percentage}% of mentions</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Insight Box */}
            <div className="p-4 rounded-lg dark:bg-blue-900/20 bg-blue-50 border border-blue-500/20">
              <p className="text-sm dark:text-blue-200 text-blue-800">
                💡 <strong>Insight:</strong> These metrics are derived entirely from chat interactions and product suggestions made by Mentoros.
              </p>
            </div>
          </div>
        )}
        
        {modalContent?.type === 'salesPerformance' && dashboardData && (
          <div className="space-y-6">
            {/* Sales Performance */}
            <div>
              <h3 className="text-lg font-semibold dark:text-text-primary text-light-text-primary mb-1">Purchase Intent</h3>
              <p className="text-xs dark:text-text-secondary text-light-text-secondary mb-4">
                Conversations that reached the point of adding to a cart. This is the assistant's
                funnel, not the store's sales — for orders actually credited to it, see the value
                scorecard above.
              </p>
              
              {/* Conversion Rate - Primary Metric */}
              <div className="dark:bg-surface bg-light-surface p-6 rounded-lg border dark:border-border-color border-light-border-color mb-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm dark:text-text-secondary text-light-text-secondary mb-1">Conversion Rate</p>
                    <p className="text-4xl font-bold text-green-500">{(dashboardData?.data?.salesPerformance?.conversionRate || 0).toFixed(1)}%</p>
                  </div>
                  <span className={`text-xs rounded-full px-3 py-1 font-medium ${salesPerfData.deltaPct >= 0 ? 'bg-green-500/15 text-green-600 dark:text-green-300' : 'bg-red-500/15 text-red-600 dark:text-red-300'}`}>
                    {salesPerfData.deltaPct >= 0 ? '↑' : '↓'} {Math.abs(salesPerfData.deltaPct).toFixed(1)}%
                  </span>
                </div>
                <p className="text-xs dark:text-text-secondary text-light-text-secondary">Percentage of sessions resulting in orders</p>
              </div>

              {/* Sales Performance Grid - 4 KPIs */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-4 dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color">
                  <p className="text-xs uppercase tracking-wider dark:text-text-secondary text-light-text-secondary mb-2">Carts Started</p>
                  <p className="text-3xl font-bold text-primary">{dashboardData?.data?.salesPerformance?.ordersGenerated || 0}</p>
                  <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">30 days</p>
                  <div className="mt-2">
                    <span className={`text-xs font-medium ${salesPerfData.ordersDelta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {salesPerfData.ordersDelta >= 0 ? '↑' : '↓'} {Math.abs(salesPerfData.ordersDelta).toFixed(1)}%
                    </span>
                  </div>
                </div>
                
                <div className="p-4 dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color">
                  <p className="text-xs uppercase tracking-wider dark:text-text-secondary text-light-text-secondary mb-2">Avg Cart Value</p>
                  <p className="text-3xl font-bold text-green-500">€{(dashboardData?.data?.salesPerformance?.avgOrderValue || 0).toFixed(2)}</p>
                  <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">30 days</p>
                  <div className="mt-2">
                    <span className={`text-xs font-medium ${salesPerfData.avgOrderValueDelta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {salesPerfData.avgOrderValueDelta >= 0 ? '↑' : '↓'} {Math.abs(salesPerfData.avgOrderValueDelta).toFixed(1)}%
                    </span>
                  </div>
                </div>
                
                <div className="p-4 dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color">
                  <p className="text-xs uppercase tracking-wider dark:text-text-secondary text-light-text-secondary mb-2">Avg Products per Cart</p>
                  <p className="text-3xl font-bold text-blue-500">{(dashboardData?.data?.salesPerformance?.avgProductsPerOrder || 0).toFixed(1)}</p>
                  <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">items per cart</p>
                </div>
                
                <div className="p-4 dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color">
                  <p className="text-xs uppercase tracking-wider dark:text-text-secondary text-light-text-secondary mb-2">Messages to First Cart</p>
                  <p className="text-3xl font-bold text-purple-500">{(dashboardData?.data?.salesPerformance?.avgMessagesToFirstOrder || 0).toFixed(1)}</p>
                  <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">messages before adding to cart</p>
                </div>
              </div>
            </div>

            {/* Insight */}
            <div className="p-4 rounded-lg dark:bg-blue-900/20 bg-blue-50 border border-blue-500/20">
              <p className="text-sm dark:text-blue-200 text-blue-800">
                💡 <strong>Conversion Rate</strong> here is the share of conversations that reached a cart — an
                intent signal, not a sale. Orders the assistant can actually be credited with are in
                the value scorecard, measured against the store's own orders.
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DashboardPage;
